/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React, { useCallback } from 'react';

import { css, jsx } from '@atlaskit/css';
import {
	sharedPluginStateHookMigratorFactory,
	useSharedPluginState,
} from '@atlaskit/editor-common/hooks';
import { type PublicPluginAPI, type ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIDefinitionsPlugin } from '../aiDefinitionsPluginType';
import { closeReadingAidsPopup } from '../pm-plugins/ai-definitions/commands';
import type { ReadingAidsPopup, ReadingAidsPopupPropsInState } from '../types';

import { AIDefinitionsGlobalStyles } from './GlobalStyles';
import { READING_AIDS_POPUP_PORTAL_CONTAINER } from './utils/const';
import { useManageSelectionToolbarVisibility } from './utils/hooks';

const readingAidsPopupPortalContainerStyle = css({
	position: 'relative',
});

type CanShowReadingAidsPopup =
	| {
			show: false;
			contentId: undefined;
			ReadingAidsPopup: undefined;
			isReadingAidsPopupOpen: false;
			readingAidsPopupKey: undefined;
			readingAidsPopupProps: undefined;
	  }
	| {
			show: true;
			// need to add the following properties to make typescript happy when accessing the properties
			contentId: string;
			ReadingAidsPopup: ReadingAidsPopup;
			isReadingAidsPopupOpen: true;
			readingAidsPopupKey: string;
			readingAidsPopupProps: ReadingAidsPopupPropsInState;
	  };

function canShowReadingAidsPopup({
	props,
	enabled,
	autoHighlightEnabled,
	readingAidsPopupProps,
	readingAidsPopupKey,
	isReadingAidsPopupOpen,
}: {
	props: AIDefinitionsProps;
	enabled?: boolean;
	autoHighlightEnabled?: boolean;
	readingAidsPopupProps?: ReadingAidsPopupPropsInState;
	readingAidsPopupKey?: string;
	isReadingAidsPopupOpen?: boolean;
}): CanShowReadingAidsPopup {
	const { contentId, ReadingAidsPopup, editorView } = props;
	if (
		editorView &&
		enabled !== undefined &&
		autoHighlightEnabled !== undefined &&
		contentId &&
		ReadingAidsPopup &&
		isReadingAidsPopupOpen &&
		readingAidsPopupKey &&
		readingAidsPopupProps
	) {
		return {
			show: true,
			contentId,
			ReadingAidsPopup,
			isReadingAidsPopupOpen,
			readingAidsPopupKey,
			readingAidsPopupProps,
		};
	}
	return {
		show: false,
		contentId: undefined,
		ReadingAidsPopup: undefined,
		isReadingAidsPopupOpen: false,
		readingAidsPopupKey: undefined,
		readingAidsPopupProps: undefined,
	};
}

export type AIDefinitionsProps = {
	// api is mandatory but can be undefined
	api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
	editorView: EditorView;
	contentId?: string;
	ReadingAidsPopup?: ReadingAidsPopup;
};

const useSharedState = sharedPluginStateHookMigratorFactory(
	(api: PublicPluginAPI<[AIDefinitionsPlugin]> | undefined) => {
		const enabled = useSharedPluginStateSelector(api, 'aiDefinitions.enabled');
		const autoHighlightEnabled = useSharedPluginStateSelector(
			api,
			'aiDefinitions.autoHighlightEnabled',
		);
		const readingAidsPopupProps = useSharedPluginStateSelector(
			api,
			'aiDefinitions.readingAidsPopupProps',
		);
		const readingAidsPopupKey = useSharedPluginStateSelector(
			api,
			'aiDefinitions.readingAidsPopupKey',
		);
		const isReadingAidsPopupOpen = useSharedPluginStateSelector(
			api,
			'aiDefinitions.isReadingAidsPopupOpen',
		);
		const mode = useSharedPluginStateSelector(api, 'editorViewMode.mode');
		return {
			enabled,
			autoHighlightEnabled,
			readingAidsPopupProps,
			readingAidsPopupKey,
			isReadingAidsPopupOpen,
			mode,
		};
	},
	(api: PublicPluginAPI<[AIDefinitionsPlugin]> | undefined) => {
		const { aiDefinitionsState } = useSharedPluginState(api, ['aiDefinitions']);
		const { editorViewModeState } = useSharedPluginState(api, ['editorViewMode']);
		return {
			enabled: aiDefinitionsState?.enabled,
			autoHighlightEnabled: aiDefinitionsState?.autoHighlightEnabled,
			readingAidsPopupProps: aiDefinitionsState?.readingAidsPopupProps,
			readingAidsPopupKey: aiDefinitionsState?.readingAidsPopupKey,
			isReadingAidsPopupOpen: aiDefinitionsState?.isReadingAidsPopupOpen,
			mode: editorViewModeState?.mode,
		};
	},
);

/**
 * readingAidsPopupKey
 * - When there are multiple auto-highlights in the document,
 * - and the user clicks on one auto-highlight to trigger the ReadingAidsPopup, then click another.
 * - readingAidsPopupKey will prevent the first ReadingAidsPopup from switching to the loading state
 * - and let the second ReadingAidsPopup show loading state.
 *
 * - Right now we're hiding the first ReadingAidsPopup when we click the second one.
 * - But readingAidsPopupKey will save us an experiment check if we want to show the second ReadingAidsPopup in the future.
 */
export const AIDefinitions = (props: AIDefinitionsProps) => {
	const { api, editorView } = props;
	const {
		enabled: sharedEnabled,
		autoHighlightEnabled: sharedAutoHighlightEnabled,
		readingAidsPopupProps: sharedReadingAidsPopupProps,
		readingAidsPopupKey: sharedReadingAidsPopupKey,
		isReadingAidsPopupOpen: sharedIsReadingAidsPopupOpen,
		mode,
	} = useSharedState(api);
	const defaultOnResult = useCallback(() => {}, []);

	const onLoad = useCallback(() => {
		sharedReadingAidsPopupProps?.onLoad?.();
		// maybe log analytics event here in the future if needed
	}, [sharedReadingAidsPopupProps]);

	const defaultOnClose = useCallback(() => {
		closeReadingAidsPopup()(editorView.state, editorView.dispatch);
	}, [editorView]);

	const { show, contentId, ReadingAidsPopup, readingAidsPopupKey, readingAidsPopupProps } =
		canShowReadingAidsPopup({
			props,
			enabled: sharedEnabled,
			autoHighlightEnabled: sharedAutoHighlightEnabled,
			readingAidsPopupProps: sharedReadingAidsPopupProps,
			readingAidsPopupKey: sharedReadingAidsPopupKey,
			isReadingAidsPopupOpen: sharedIsReadingAidsPopupOpen,
		});

	useManageSelectionToolbarVisibility({ api, show });

	if (!sharedEnabled || !sharedAutoHighlightEnabled) {
		return null;
	}

	if (editorExperiment('editor_ai_contextual_selection_toolbar_button', 'control')) {
		if (mode !== 'view') {
			return null;
		}
	}

	return (
		<>
			<AIDefinitionsGlobalStyles />
			<div id={READING_AIDS_POPUP_PORTAL_CONTAINER} css={readingAidsPopupPortalContainerStyle} />
			{show ? (
				<ReadingAidsPopup
					key={readingAidsPopupKey}
					// Ignored via go/ees005
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...readingAidsPopupProps}
					isReadingAidsOpen
					popupPortalContainerId={READING_AIDS_POPUP_PORTAL_CONTAINER}
					contentId={contentId}
					onResult={readingAidsPopupProps?.onResult || defaultOnResult}
					onLoad={onLoad}
					onClose={readingAidsPopupProps?.onClose || defaultOnClose}
				/>
			) : null}
		</>
	);
};
